import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Swiper, SwiperSlide } from "swiper/react";

import { DEFAULT_PRODUCT_IMAGE } from "../../constants/images";
import { toVND } from "../../helpers/helpers";
import { resolveApiMessage } from "../../helpers/apiMessage";
import { getMenuCategories, getMenus, getMenuOptions } from "../../slices/thunks";
import { AppDispatch } from "../../store";
import { initialState as menuOptionsInitialState } from "../../slices/MenuOptions/reducer";
import { showAlertWithTimeout } from "../../slices/Alert/reducer";
import { createMenuOptionLookup, getMenuOptionDisplayItems } from "../../helpers/menuOptions";
import { deleteMenu } from "../../slices/Menu/thunk";
import { clearMenuError } from "../../slices/Menu/reducer";
import ConfirmModal from "../../Components/Common/ConfirmModal";
import Alert from "../../Components/Common/Alert";
import HeaderSidebarToggle from "../../Components/Common/HeaderSidebarToggle";
import HomeLogoLink from "../../Components/Common/HomeLogoLink";

const getCategoryId = (category: any): string | null => {
    if (!category) {
        return null;
    }

    const candidates = [
        category.id,
        category.slug,
        category.code,
        category.key,
        category.uuid,
    ];

    for (const value of candidates) {
        if (value !== undefined && value !== null) {
            return String(value);
        }
    }

    if (typeof category.name === "string" && category.name.trim().length > 0) {
        return category.name;
    }

    if (typeof category.title === "string" && category.title.trim().length > 0) {
        return category.title;
    }

    return null;
};

const getCategoryName = (category: any): string => {
    if (!category) {
        return "Danh mục";
    }

    return (
        category.name ??
        category.title ??
        category.label ??
        category.displayName ??
        category.slug ??
        "Danh mục"
    );
};

const getCategorySortOrder = (category: any): number | undefined => {
    if (!category || typeof category !== "object") {
        return undefined;
    }

    const candidates = [
        (category as any).sort_order,
        (category as any).sortOrder,
        (category as any).order,
    ];

    for (const value of candidates) {
        if (typeof value === "number" && Number.isFinite(value)) {
            return value;
        }

        if (typeof value === "string") {
            const numeric = Number(value);
            if (!Number.isNaN(numeric)) {
                return numeric;
            }
        }
    }

    return undefined;
};

const isCategoryCandidate = (category: any): boolean => {
    if (!category || typeof category !== "object") {
        return false;
    }

    return (
        "menus" in category ||
        "sort_order" in category ||
        "sortOrder" in category ||
        "parent_id" in category
    );
};

const getMenuCategoryIds = (menu: any): string[] => {
    if (!menu) {
        return [];
    }

    if (Array.isArray(menu.categories)) {
        const ids = menu.categories
            .map((category: any) => getCategoryId(category))
            .filter((value: string | null): value is string => Boolean(value));
        if (ids.length > 0) {
            return Array.from(new Set(ids));
        }
    }

    const directKeys = [
        menu.categoryId,
        menu.category_id,
        menu.categoryID,
        menu.categorySlug,
    ];

    for (const value of directKeys) {
        if (value !== undefined && value !== null) {
            return [String(value)];
        }
    }

    if (typeof menu.category === "string" && menu.category.trim().length > 0) {
        return [menu.category];
    }

    if (menu.category && typeof menu.category === "object") {
        const categoryId = getCategoryId(menu.category);
        return categoryId ? [categoryId] : [];
    }

    return [];
};

const getMenuCategoryName = (menu: any): string => {
    if (!menu) {
        return "Danh mục";
    }

    const { category } = menu;
    if (typeof category === "string" && category.trim().length > 0) {
        return category;
    }

    if (category && typeof category === "object") {
        return getCategoryName(category);
    }

    return menu.categoryName ?? menu.category_name ?? "Danh mục";
};

const getMenuIdentifier = (menu: any): string | null => {
    if (!menu || typeof menu !== "object") {
        return null;
    }

    const candidates = [
        (menu as any).id,
        (menu as any).menuId,
        (menu as any).menu_id,
        (menu as any).uuid,
        (menu as any).slug,
        (menu as any).code,
    ];

    for (const value of candidates) {
        if (value !== undefined && value !== null && value !== "") {
            return String(value);
        }
    }

    return null;
};

const getMenuId = (menu: any, fallbackIndex: number): string => {
    const identifier = getMenuIdentifier(menu);
    if (identifier) {
        return identifier;
    }

    return `menu-${fallbackIndex}`;
};

const getMenuName = (menu: any): string => {
    if (!menu) {
        return "Món";
    }

    return menu.name ?? menu.title ?? menu.productName ?? "Món";
};

const getMenuPrice = (menu: any): number => {
    if (!menu) {
        return 0;
    }

    const candidates = [
        menu.price,
        menu.basePrice,
        menu.base_price,
        menu.cost,
        menu.amount,
    ];

    for (const value of candidates) {
        if (value === undefined || value === null) {
            continue;
        }

        if (typeof value === "number") {
            return value;
        }

        const numeric = Number(value);
        if (!Number.isNaN(numeric)) {
            return numeric;
        }
    }

    return 0;
};

const getMenuImage = (menu: any): string => {
    if (!menu) {
        return DEFAULT_PRODUCT_IMAGE;
    }

    const candidates = [
        menu.image,
        menu.imageUrl,
        menu.image_url,
        menu.thumbnail,
        menu.thumbnailUrl,
    ];

    for (const value of candidates) {
        if (typeof value === "string" && value.trim().length > 0) {
            return value;
        }
    }

    return DEFAULT_PRODUCT_IMAGE;
};

const parseBooleanLike = (value: any): boolean | undefined => {
    if (typeof value === "boolean") {
        return value;
    }

    if (typeof value === "number") {
        if (Number.isNaN(value)) {
            return undefined;
        }
        return value !== 0;
    }

    if (typeof value === "string") {
        const normalized = value.trim().toLowerCase();
        if (normalized.length === 0) {
            return undefined;
        }

        const truthyValues = ["true", "1", "available", "in_stock", "instock", "active", "on"];
        const falsyValues = ["false", "0", "unavailable", "out_of_stock", "inactive", "off", "sold_out"];

        if (truthyValues.includes(normalized)) {
            return true;
        }

        if (falsyValues.includes(normalized)) {
            return false;
        }
    }

    return undefined;
};

const isMenuAvailable = (menu: any): boolean => {
    if (!menu || typeof menu !== "object") {
        return true;
    }

    const candidates = [
        (menu as any).isAvailable,
        (menu as any).is_available,
        (menu as any).available,
        (menu as any).availability,
        (menu as any).status,
        (menu as any).inStock,
        (menu as any).in_stock,
    ];

    for (const candidate of candidates) {
        const parsed = parseBooleanLike(candidate);
        if (parsed !== undefined) {
            return parsed;
        }
    }

    return true;
};

const getAvailabilityBadgeClass = (available: boolean): string =>
    available ? "badge bg-success-subtle text-success" : "badge bg-danger-subtle text-danger";

const getAvailabilityLabel = (available: boolean): string => (available ? "Còn món" : "Hết món");

const Menu = () => {
    const dispatch = useDispatch<AppDispatch>();
    const navigate = useNavigate();
    const { categories, menus, loading, error } = useSelector((state: any) => state.MenuList);
    const menuOptionsState = useSelector(
        (state: any) => state.MenuOptions ?? menuOptionsInitialState
    );

    const categoriesList = useMemo(() => (Array.isArray(categories) ? categories : []), [categories]);
    const categoriesFromState = useMemo(
        () => categoriesList.filter((category) => isCategoryCandidate(category)),
        [categoriesList]
    );
    const menusList = useMemo(() => (Array.isArray(menus) ? menus : []), [menus]);
    const menuOptionsList = useMemo(
        () => (Array.isArray(menuOptionsState.options) ? menuOptionsState.options : []),
        [menuOptionsState.options]
    );

    const [activeCategoryId, setActiveCategoryId] = useState<string | null>(null);
    const [showDeleteMenuModal, setShowDeleteMenuModal] = useState(false);
    const [deleteMenuTarget, setDeleteMenuTarget] = useState<any | null>(null);

    useEffect(() => {
        dispatch(getMenus());
    }, [dispatch]);

    useEffect(() => {
        if (categoriesFromState.length === 0) {
            dispatch(getMenuCategories());
        }
    }, [dispatch, categoriesFromState.length]);

    useEffect(() => {
        if (!menuOptionsState.loading && !menuOptionsState.loaded) {
            dispatch(getMenuOptions());
        }
    }, [dispatch, menuOptionsState.loading, menuOptionsState.loaded]);

    const menuOptionLookup = useMemo(
        () => createMenuOptionLookup(menuOptionsList),
        [menuOptionsList]
    );

    const derivedCategories = useMemo(() => {
        if (categoriesFromState.length > 0) {
            return [...categoriesFromState].sort((left, right) => {
                const leftOrder = getCategorySortOrder(left);
                const rightOrder = getCategorySortOrder(right);

                if (leftOrder !== undefined && rightOrder !== undefined) {
                    if (leftOrder !== rightOrder) {
                        return leftOrder - rightOrder;
                    }
                } else if (leftOrder !== undefined) {
                    return -1;
                } else if (rightOrder !== undefined) {
                    return 1;
                }

                return getCategoryName(left).localeCompare(getCategoryName(right));
            });
        }

        const map = new Map<string, { id: string; name: string }>();
        menusList.forEach((menu: any) => {
            const categoryIds = getMenuCategoryIds(menu);
            if (categoryIds.length === 0) {
                return;
            }

            if (Array.isArray(menu.categories)) {
                menu.categories.forEach((category: any) => {
                    const categoryId = getCategoryId(category);
                    if (!categoryId) {
                        return;
                    }
                    if (!map.has(categoryId)) {
                        map.set(categoryId, {
                            id: categoryId,
                            name: getCategoryName(category),
                        });
                    }
                });
                return;
            }

            categoryIds.forEach((categoryId) => {
                if (!map.has(categoryId)) {
                    map.set(categoryId, {
                        id: categoryId,
                        name: getMenuCategoryName(menu),
                    });
                }
            });
        });

        return Array.from(map.values());
    }, [categoriesFromState, menusList]);

    useEffect(() => {
        if (derivedCategories.length === 0) {
            setActiveCategoryId(null);
            return;
        }

        setActiveCategoryId((previous) => {
            if (previous && derivedCategories.some((category) => getCategoryId(category) === previous)) {
                return previous;
            }

            const firstId = getCategoryId(derivedCategories[0]);
            return firstId ?? null;
        });
    }, [derivedCategories]);

    const selectedCategory = useMemo(() => {
        if (!activeCategoryId) {
            return undefined;
        }

        return categoriesFromState.find((category: any) => getCategoryId(category) === activeCategoryId);
    }, [categoriesFromState, activeCategoryId]);

    const itemsToRender = useMemo(() => {
        const categoryMenus = selectedCategory && Array.isArray((selectedCategory as any).menus)
            ? (selectedCategory as any).menus
            : undefined;

        if (categoryMenus) {
            return categoryMenus;
        }

        if (!activeCategoryId) {
            return menusList;
        }

        const filtered = menusList.filter((menu: any) =>
            getMenuCategoryIds(menu).includes(activeCategoryId)
        );

        if (filtered.length > 0) {
            return filtered;
        }

        return menusList;
    }, [selectedCategory, activeCategoryId, menusList]);

    const hasMenus = menusList.length > 0 || itemsToRender.length > 0;
    const showEmptyState = !loading && !hasMenus;

    const handleDeleteMenu = (menu: any) => {
        setDeleteMenuTarget(menu);
        setShowDeleteMenuModal(true);
    };

    const closeDeleteMenuModal = () => {
        setShowDeleteMenuModal(false);
        setDeleteMenuTarget(null);
    };

    const handleConfirmDeleteMenu = async () => {
        if (!deleteMenuTarget) {
            closeDeleteMenuModal();
            return;
        }

        const identifier = getMenuIdentifier(deleteMenuTarget);
        if (!identifier) {
            dispatch(showAlertWithTimeout("warning", "Cảnh báo", "Không xác định được món để xóa."));
            closeDeleteMenuModal();
            return;
        }

        const name = getMenuName(deleteMenuTarget);
        closeDeleteMenuModal();

        try {
            await dispatch(deleteMenu(identifier)).unwrap();
            dispatch(showAlertWithTimeout("success", "Thành công", `Đã xóa món "${name}".`));
        } catch (deleteError) {
            console.error(deleteError);
            const message = resolveApiMessage(deleteError, {
                fallbackMessage: "Xóa món thất bại. Vui lòng thử lại.",
            });
            dispatch(clearMenuError());
            dispatch(showAlertWithTimeout("danger", "Thất bại", message));
        }
    };

    return (
        <React.Fragment>
            <Alert />
            <ConfirmModal
                isOpen={showDeleteMenuModal}
                typeConfirm="danger"
                title="Xác nhận xóa"
                content={
                    deleteMenuTarget
                        ? `Bạn có chắc chắn muốn xóa món "${getMenuName(deleteMenuTarget)}" không?`
                        : "Bạn có chắc chắn muốn xóa món này không?"
                }
                onConfirm={handleConfirmDeleteMenu}
                onCancel={closeDeleteMenuModal}
            />
            <div className="header-area" id="headerArea">
                <div className="container h-100 d-flex text-center align-items-center justify-content-between rtl-flex-d-row-r">
                    <HomeLogoLink className="me-2" />

                    <div className="page-heading">
                        <h6 className="mb-0">Danh sách món</h6>
                    </div>

                    <div className="d-flex align-items-center">
                        <div className="filter-option"><i onClick={() => navigate('/menu/create')} className="mdi mdi-plus"></i></div>
                        <HeaderSidebarToggle className="ms-2" />
                    </div>
                </div>
            </div>

            <div className="page-content-wrapper">
                <div className="py-3">
                    <div className="container">
                        {derivedCategories.length > 0 && (
                            <div className="row g-1 align-items-center rtl-flex-d-row-r">
                                <div className="col-12 mt-n3">
                                    <Swiper
                                        loop={false}
                                        slidesPerView={2.5}
                                        spaceBetween={5}
                                        className="order-catagories owl-carousel py-1"
                                        slideToClickedSlide={true}
                                    >
                                        {derivedCategories.map((category: any, index: number) => {
                                            const id = getCategoryId(category) ?? `category-${index}`;
                                            const isActive = activeCategoryId === id;
                                            const label = getCategoryName(category);

                                            return (
                                                <SwiperSlide key={id} className={isActive ? "active" : ""}>
                                                    <Link
                                                        className="shadow-sm"
                                                        to="#"
                                                        onClick={(event) => {
                                                            event.preventDefault();
                                                            setActiveCategoryId(id);
                                                        }}
                                                    >
                                                        {label}
                                                    </Link>
                                                </SwiperSlide>
                                            );
                                        })}
                                    </Swiper>
                                </div>
                            </div>
                        )}

                        {error && (
                            <div className="alert alert-danger" role="alert">
                                {error}
                            </div>
                        )}

                        {loading ? (
                            <div className="text-center py-5">
                                <div className="spinner-border text-primary" role="status">
                                    <span className="visually-hidden">Loading...</span>
                                </div>
                                <p className="mt-2 mb-0">Đang tải dữ liệu...</p>
                            </div>
                        ) : showEmptyState ? (
                            <div className="text-center py-5">
                                <p className="mb-3">Hiện chưa có món nào trong danh sách.</p>
                                <div className="d-flex flex-wrap justify-content-center gap-2">
                                    <Link className="btn btn-primary" to="/menu/create">
                                        Tạo món ngay
                                    </Link>
                                    <Link className="btn btn-outline-primary" to="/menu/import">
                                        Nhập hàng loạt
                                    </Link>
                                </div>
                            </div>
                        ) : (
                            <div className="row g-2">
                                {itemsToRender.map((item: any, index: number) => {
                                    const key = getMenuId(item, index);
                                    const name = getMenuName(item);
                                    const price = getMenuPrice(item);
                                    const image = getMenuImage(item);
                                    const identifier = getMenuIdentifier(item);
                                    const available = isMenuAvailable(item);
                                    const optionBadges = getMenuOptionDisplayItems(
                                        item,
                                        menuOptionLookup
                                    );

                                    return (
                                        <div key={key} className="col-12">
                                            <div className="card horizontal-product-card">
                                                <div className="d-flex align-items-center">
                                                    <div className="product-thumbnail-side">
                                                        <Link className="product-thumbnail d-block" to="/menu">
                                                            <img src={image} alt={name} />
                                                        </Link>
                                                    </div>
                                                    <div className="product-description">
                                                        <p className="product-title d-block mb-1">{name}</p>

                                                        <p className="sale-price">{toVND(price)}</p>
                                                        {optionBadges.length > 0 && (
                                                            <div className="mt-1">
                                                                {optionBadges.map((option) => (
                                                                    <span
                                                                        key={option.id}
                                                                        className="badge bg-primary-subtle text-primary me-1 mb-1"
                                                                    >
                                                                        {option.name}
                                                                    </span>
                                                                ))}
                                                            </div>
                                                        )}
                                                        <div className="d-flex align-items-center justify-content-between mt-2">
                                                            <span className={getAvailabilityBadgeClass(available)}>
                                                                {getAvailabilityLabel(available)}
                                                            </span>
                                                            {identifier && (
                                                                <div className="d-flex align-items-center gap-2 ms-3">
                                                                    <Link
                                                                        className="btn btn-sm btn-outline-primary"
                                                                        to={`/menu/${encodeURIComponent(identifier)}/edit`}
                                                                    >
                                                                        Sửa
                                                                    </Link>
                                                                    <button
                                                                        type="button"
                                                                        className="btn btn-sm btn-outline-danger"
                                                                        onClick={() => handleDeleteMenu(item)}
                                                                    >
                                                                        Xóa
                                                                    </button>
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </React.Fragment>
    );
};

export default Menu;
